import {
  useColorMode,
  GridItem,
  AbsoluteCenter,
  Fade,
  Spinner,
  Text,
  useColorModeValue,
  Box,
  Heading,
  Image,
  Button,
  useToast,
} from '@chakra-ui/react';
import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  faEyeSlash,
  faMousePointer,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as IsolationIcon } from '../../../assets/icons/isolation.svg';
import SparkelIcon from '../../../components/common/icon/SparkelIcon';
import noSheetsPlaceholder from '../../../assets/img/no-sheets-placeholder.svg';
import PlaceholderText from '../../text/PlaceholderText';
import { usePdfOpacity } from './hooks/usePdfOpacity';
import DragAndDrop from 'src/components/dragAndDrop/DragAndDrop';
import { useSheets } from 'src/hooks/sheets';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import {
  SelectedDbIds,
  useSelectedDbIds,
} from 'src/components/viewers/common/hooks/useSelectedDbIds';
import {
  SheetShapesManager,
  useSheetShapesManager,
} from 'src/components/viewers/common/hooks/useSheetShapesManager';
import {
  SheetsVisibilityManager,
  useSheetsVisibility,
} from 'src/components/viewers/common/hooks/useSheetsVisibilityManager';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import {
  ActiveSheet,
  useActiveSheet,
} from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ActiveSheetPagination } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheetPagination';
import {
  SheetViewerV2Calibration,
  useCalibration,
} from 'src/components/viewers/SheetsViewerV2/hooks/useCalibration';
import { useMeasurementTool } from 'src/components/viewers/SheetsViewerV2/hooks/useMeasurementTool';
import { useShapes } from 'src/components/viewers/SheetsViewerV2/hooks/useShapes';
import { useSheetsAggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { Sheet } from 'src/hooks/sheets';
import { useShapesDrawingTool } from 'src/components/viewers/SheetsViewerV2/hooks/useShapesDrawingTool';
import {
  ContextMenuContextType,
  ContextMenuProvider,
  OnShowProps,
  PredicateParams,
  useCustomContextMenu,
} from 'src/services/viewer/CustomContextMenu';
import {
  DbIdsPerModel,
  SheetShapesMap,
} from 'src/components/common/ForgeViewer';
import { isShapeHidden } from 'src/components/SheetShapes';
import AggregatedView = Autodesk.Viewing.AggregatedView;
import { setTheme } from 'src/components/viewers/SheetsViewerV2/utils/theme';
import { useLocalStorage } from 'src/hooks/local-storage';
import {
  SheetsColorizationManager,
  useSheetsColorization,
} from 'src/components/viewers/common/hooks/useSheetsColorizationManager';
import {
  GetProjectSettingsDocument,
  GetSparkelPropertiesForProjectDocument,
  GetSparkelPropertiesForProjectQuery,
  LengthUnitTypeEnum,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { ChakraColor } from 'src/utils/color-util';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { VIEWER_TOP_OFFSET } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';

export type SheetsViewerV2ContextType = SelectedDbIds & {
  projectId: string;
  viewer: {
    elementRef: MutableRefObject<HTMLDivElement | null>;
    aggregated: AggregatedView | null;
  };
  viewerMode: ViewerMode;
  activeSheet: ActiveSheet | null;
  activeSheetId: string | null;
  changeActiveSheet: (sheetId: string | null) => void;
  sheets: Sheet[];
  pagination: ActiveSheetPagination;
  setPageNumber: (pageNumber: number) => void;
  calibration: SheetViewerV2Calibration;
  shapesManager: SheetShapesManager;
  visibilityManager: SheetsVisibilityManager;
  /** Legends auto-hidden because they don't match the active order */
  autoHiddenLegends: DbIdsPerModel;
  activeTool: ActiveTool;
  setActiveTool: Dispatch<SetStateAction<ActiveTool>>;
  measurement: { isMeasuring: boolean };
  drawing: ReturnType<typeof useShapesDrawingTool>;
  pdfGhosting: {
    opacity: number;
    setOpacity: (opacity: number) => void;
  };
  showSheetShapeLabels: boolean;
  setShowSheetShapeLabels: (value: boolean) => void;
  colorization: SheetsColorizationManager;
  propertyData: GetSparkelPropertiesForProjectQuery | undefined;
  isLoading: boolean;
};

export const SheetsViewerV2Context =
  createContext<SheetsViewerV2ContextType | null>(null);

type Props = {
  projectId: string;
  shouldHideViewer: boolean;
  children: ReactElement;
};

// Sheet-specific context menu types
export type SheetViewerV2MenuProps = {
  selections: DbIdsPerModel;
  hasSelectedElements: boolean;
  target?: {
    type: 'SHAPE';
    modelUrn: string;
    dbId: number;
  };
};

export const SheetViewerV2ContextMenuContext = createContext<
  ContextMenuContextType<SheetViewerV2MenuProps>
>({
  registerItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  unregisterItem: () => {
    throw new Error('No Context Menu Context provider found.');
  },
  items: [],
});

export const SheetViewerV2ContextMenuProvider = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  return (
    <ContextMenuProvider context={SheetViewerV2ContextMenuContext}>
      {children}
    </ContextMenuProvider>
  );
};

// Simple point-in-polygon test using ray casting algorithm
function pointInPolygon(
  point: { x: number; y: number },
  polygon: { x: number; y: number }[]
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    if (
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

// Check if point is near a line segment
function pointNearLine(
  point: { x: number; y: number },
  line: { x: number; y: number }[],
  threshold: number
): boolean {
  for (let i = 0; i < line.length - 1; i++) {
    const p1 = line[i];
    const p2 = line[i + 1];
    const dist = distanceToLineSegment(point, p1, p2);
    if (dist < threshold) return true;
  }
  return false;
}

// Distance from point to line segment
function distanceToLineSegment(
  point: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const A = point.x - p1.x;
  const B = point.y - p1.y;
  const C = p2.x - p1.x;
  const D = p2.y - p1.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = p1.x;
    yy = p1.y;
  } else if (param > 1) {
    xx = p2.x;
    yy = p2.y;
  } else {
    xx = p1.x + param * C;
    yy = p1.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

// Hit test a shape against a point in API coordinates
function hitTestShape(
  shape: SheetShapeDeepFragment,
  point: { x: number; y: number },
  lineThreshold: number
): boolean {
  if (shape.sheetShapePolygon) {
    // Test against all polygons in the multipolygon
    for (const polygon of shape.sheetShapePolygon.multipolygon.polygons) {
      const exterior = polygon.exterior.points.map(
        (p: { x: number; y: number }) => ({
          x: p.x,
          y: p.y,
        })
      );
      if (pointInPolygon(point, exterior)) {
        return true;
      }
    }
  } else if (shape.sheetShapeLine) {
    const linePoints = shape.sheetShapeLine.points.map(
      (p: { x: number; y: number }) => ({
        x: p.x,
        y: p.y,
      })
    );
    if (pointNearLine(point, linePoints, lineThreshold)) {
      return true;
    }
  } else if (shape.sheetShapePoint) {
    const shapePoint = shape.sheetShapePoint.point;
    const dist = Math.sqrt(
      Math.pow(point.x - shapePoint.x, 2) + Math.pow(point.y - shapePoint.y, 2)
    );
    if (dist < lineThreshold * 2) {
      return true;
    }
  } else if (shape.sheetShapeSplitLine) {
    const linePoints = shape.sheetShapeSplitLine.points.map(
      (p: { x: number; y: number }) => ({
        x: p.x,
        y: p.y,
      })
    );
    if (pointNearLine(point, linePoints, lineThreshold)) {
      return true;
    }
  }
  return false;
}

// Get shape priority for hit testing (higher priority = preferred on overlap)
function getShapePriority(shape: SheetShapeDeepFragment): number {
  if (shape.sheetShapePoint) return 4;
  if (shape.sheetShapeLine) return 3;
  if (shape.sheetShapeSplitLine) return 2;
  if (shape.sheetShapePolygon) return 1;
  return 0;
}

// Find the shape under the cursor using API coordinates
function findShapeAtPoint(
  apiPoint: { x: number; y: number },
  renderedSheetShapes: SheetShapesMap,
  visibilityManager: SheetsVisibilityManager,
  lineThreshold: number
): SheetShapeDeepFragment | null {
  const allShapes = Object.entries(renderedSheetShapes).flatMap(
    ([urn, shapes]) => shapes.map((shape) => ({ ...shape, urn }))
  );

  const hitShapes = allShapes.filter((shape) => {
    if (
      isShapeHidden(
        visibilityManager.hiddenElements,
        shape.urn,
        shape,
        visibilityManager.hiddenSheetShapes,
        visibilityManager.isolatedElements,
        false
      )
    ) {
      return false;
    }
    return hitTestShape(shape, apiPoint, lineThreshold);
  });

  hitShapes.sort((a, b) => getShapePriority(b) - getShapePriority(a));
  return hitShapes.length > 0 ? hitShapes[0] : null;
}

// Get mouse coordinates from event (returns null for keyboard events)
function getEventCoordinates(
  event:
    | React.MouseEvent<HTMLDivElement>
    | React.TouchEvent<HTMLDivElement>
    | React.KeyboardEvent<HTMLDivElement>
): { clientX: number; clientY: number } | null {
  if (event.type === 'mouseup') {
    const mouseEvent = event as React.MouseEvent<HTMLDivElement>;
    return { clientX: mouseEvent.clientX, clientY: mouseEvent.clientY };
  }
  if (event.type === 'touchstart') {
    const touch = (event as React.TouchEvent<HTMLDivElement>).touches[0];
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
  // Keyboard events don't have coordinates
  return null;
}

// Find shape at cursor position using viewer coordinates
function findShapeAtCursor(
  clientX: number,
  clientY: number,
  aggregatedView: AggregatedView | null | undefined,
  activeSheetData: ActiveSheetData | null | undefined,
  renderedSheetShapes: SheetShapesMap,
  visibilityManager: SheetsVisibilityManager
): SheetShapeDeepFragment | null {
  if (!aggregatedView?.viewer || !activeSheetData?.viewport) {
    return null;
  }

  const adjustedY = clientY - VIEWER_TOP_OFFSET;
  const worldResult = aggregatedView.viewer.impl.clientToWorld(
    clientX,
    adjustedY
  );

  if (!worldResult?.point) {
    return null;
  }

  const converter = new ApiCoordinatesConverter(
    aggregatedView.viewer,
    activeSheetData.viewport
  );
  const apiPoint = converter.convertToApiPoint(worldResult.point);
  return findShapeAtPoint(apiPoint, renderedSheetShapes, visibilityManager, 10);
}

// Hook to create a context menu for SheetViewerV2 that works with sheet shapes
function useSheetViewerV2ContextMenu(
  viewerRef: React.RefObject<HTMLDivElement>,
  visibilityManager: SheetsVisibilityManager,
  selectedDbIds: DbIdsPerModel,
  setSelectedDbIds: (dbIds: DbIdsPerModel) => void,
  renderedSheetShapes: SheetShapesMap,
  activeTool?: ActiveTool,
  aggregatedView?: AggregatedView | null,
  activeSheetData?: ActiveSheetData | null
) {
  const showAndSelect = useCallback(
    async ({ event, showContextMenu }: OnShowProps<SheetViewerV2MenuProps>) => {
      if (!viewerRef.current || activeTool === ActiveTool.Drawing) {
        return;
      }

      const coords = getEventCoordinates(event);
      const currentSelection = Object.values(selectedDbIds).flat();

      // Only try to find shape under cursor if we have coordinates (mouse/touch events)
      const clickedShape = coords
        ? findShapeAtCursor(
            coords.clientX,
            coords.clientY,
            aggregatedView,
            activeSheetData,
            renderedSheetShapes,
            visibilityManager
          )
        : null;

      // Check if the clicked shape is already in the selection
      const isClickedShapeInSelection =
        clickedShape &&
        selectedDbIds[clickedShape.urn]?.includes(clickedShape.dbId);

      if (clickedShape && !isClickedShapeInSelection) {
        setSelectedDbIds({ [clickedShape.urn]: [clickedShape.dbId] });
        showContextMenu({
          event,
          props: {
            selections: { [clickedShape.urn]: [clickedShape.dbId] },
            hasSelectedElements: true,
            target: {
              type: 'SHAPE',
              dbId: clickedShape.dbId,
              modelUrn: clickedShape.urn,
            },
          },
        });
      } else if (currentSelection.length === 1) {
        showContextMenu({
          event,
          props: {
            hasSelectedElements: true,
            selections: selectedDbIds,
            target: {
              type: 'SHAPE',
              dbId: currentSelection[0],
              modelUrn: Object.keys(selectedDbIds)[0],
            },
          },
        });
      } else if (currentSelection.length > 1) {
        showContextMenu({
          event,
          props: { hasSelectedElements: true, selections: selectedDbIds },
        });
      } else {
        showContextMenu({ event });
      }
    },
    [
      selectedDbIds,
      setSelectedDbIds,
      viewerRef,
      activeTool,
      aggregatedView,
      activeSheetData,
      renderedSheetShapes,
      visibilityManager,
    ]
  );

  const { items } = useSheetViewerV2ContextMenuContext();

  return useCustomContextMenu(showAndSelect, items, [
    'viewer visibility',
    'viewer selection',
  ]);
}

export const useSheetViewerV2ContextMenuContext = () =>
  useContext(SheetViewerV2ContextMenuContext);

// eslint-disable-next-line complexity
export function SheetsViewerV2ContextProvider(props: Props) {
  const [activeTool, setActiveToolState] = useState(ActiveTool.Default);
  const setActiveTool = useCallback<Dispatch<SetStateAction<ActiveTool>>>(
    (value) => {
      setActiveToolState(value);
    },
    []
  );
  const activeToolContext = { activeTool, setActiveTool };
  const { projectId, children, shouldHideViewer } = props;
  const { viewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const { viewerRef, aggregatedView } = useSheetsAggregatedView();
  const { colorMode } = useColorMode();
  const location = useLocation();
  const navigate = useNavigate();
  const activeSheetContext = useActiveSheet({ projectId, aggregatedView });
  const pdfGhosting = usePdfOpacity({
    sheetId: activeSheetContext.activeSheetId,
    pageNumber: activeSheetContext.pagination.pageNumber,
  });
  const calibration = useCalibration({
    aggregatedView,
    ...activeToolContext,
    ...activeSheetContext,
  });
  const visibilityManager = useSheetsVisibility();
  const selectDbIds = useSelectedDbIds();
  const [showSheetShapeLabels, setShowSheetShapeLabels] = useLocalStorage(
    'showSheetShapeLabels',
    true
  );

  const colorization = useSheetsColorization();

  const [{ data: sparkelPropertiesQueryData }] = useQuery({
    query: GetSparkelPropertiesForProjectDocument,
    variables: { projectId },
  });

  // Query project settings to get the length unit
  const [{ data: projectSettingsData }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId },
  });

  // Compute display unit suffix from project settings
  const displayUnitSuffix = useMemo(() => {
    const lengthUnit = projectSettingsData?.project?.settings?.unitSettings
      ?.length?.units as LengthUnitTypeEnum | undefined;
    switch (lengthUnit) {
      case LengthUnitTypeEnum.DecimalFeet:
        return 'ft';
      case LengthUnitTypeEnum.DecimalInches:
        return 'in';
      case LengthUnitTypeEnum.Millimeters:
        return 'mm';
      case LengthUnitTypeEnum.Centimeters:
        return 'cm';
      case LengthUnitTypeEnum.Decimeters:
        return 'dm';
      case LengthUnitTypeEnum.Meters:
      default:
        return 'm';
    }
  }, [projectSettingsData?.project?.settings?.unitSettings?.length?.units]);

  // Use a stable reference with useMemo - same pattern as V1
  const propertyData = useMemo(
    () => sparkelPropertiesQueryData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sparkelPropertiesQueryData)]
  );

  const measurement = useMeasurementTool({
    activeTool,
    setActiveTool,
    viewer: aggregatedView?.viewer,
    calibration: calibration.existing,
    activeSheetData: activeSheetContext.activeSheet?.data || null,
  });

  // Create shapes manager for managing sheet shapes
  const shapesManager = useSheetShapesManager();

  // Light: gray.50. Dark: #e3e5ef (light color that inverts to dark via container's invert(1) hue-rotate(180deg))
  const pdfOverlayBackgroundColor = useColorModeValue(
    ChakraColor.gray50,
    '#e3e5ef'
  );
  const pdfOverlayConfig = useMemo(
    () => ({
      opacity: pdfGhosting.opacity,
      backgroundColor: pdfOverlayBackgroundColor,
    }),
    [pdfGhosting.opacity, pdfOverlayBackgroundColor]
  );
  const { shapesManager: shapesManagerFromHook, autoHiddenLegends } = useShapes(
    aggregatedView?.viewer,
    activeTool,
    activeSheetContext.activeSheet,
    selectDbIds,
    visibilityManager,
    showSheetShapeLabels,
    colorization,
    calibration,
    shapesManager,
    propertyData,
    calibration.existing || undefined,
    pdfOverlayConfig
  );

  // Use the shapes manager from useShapes (which is the same as the one we passed in)
  void shapesManagerFromHook; // Mark as intentionally unused

  const drawing = useShapesDrawingTool({
    activeTool,
    projectId,
    viewer: aggregatedView?.viewer,
    activeSheetData: activeSheetContext.activeSheet?.data || null,
    calibration: calibration.existing,
    renderedSheetShapes: shapesManager.renderedSheetShapes,
    propertyData,
    displayUnitSuffix,
  });

  // Create context menu for SheetViewerV2 with sheet shapes
  const { menuElement, bind: bindContextMenu } = useSheetViewerV2ContextMenu(
    viewerRef,
    visibilityManager,
    selectDbIds.selectedDbIds,
    selectDbIds.setSelectedDbIds,
    shapesManager.renderedSheetShapes,
    activeTool,
    aggregatedView,
    activeSheetContext.activeSheet?.data
  );

  // Register sheet-specific context menu items
  const { registerItem, unregisterItem } = useSheetViewerV2ContextMenuContext();
  const { t } = useTranslation('project');

  // Determine loading state - show loading when viewer is initializing or sheet is loading
  const isLoading =
    !aggregatedView || activeSheetContext.activeSheet?.isLoading;

  // Sheet upload support (drag-and-drop + upload button)
  const { createSheet } = useSheets(projectId);
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleUploadSheetFiles = useCallback(
    (files: FileList) => {
      void Promise.all(
        [...files].map(async (file) => {
          try {
            const created = await createSheet(file, file.name, 1);
            if (created?.id) {
              activeSheetContext.changeActiveSheet(created.id);
            }
            toast({
              status: 'success',
              duration: 3000,
              title: t('toast.success.sheet-upload'),
            });
          } catch {
            toast({
              status: 'error',
              duration: 3000,
              title: t('toast.error.sheet-upload'),
            });
          }
        })
      );
    },
    [createSheet, activeSheetContext.changeActiveSheet, toast, t]
  );

  const handleSheetInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleUploadSheetFiles(e.target.files);
      }
    },
    [handleUploadSheetFiles]
  );

  const selectAllVisibleHandler = useCallback(() => {
    const visibleShapes = Object.fromEntries(
      Object.keys(shapesManager.renderedSheetShapes).map((urn) => [
        urn,
        shapesManager.renderedSheetShapes[urn]
          .filter(
            (shape) =>
              !isShapeHidden(
                visibilityManager.hiddenElements,
                urn,
                shape,
                visibilityManager.hiddenSheetShapes,
                visibilityManager.isolatedElements,
                false
              )
          )
          .map((shape) => shape.dbId),
      ])
    );
    selectDbIds.setSelectedDbIds(visibleShapes);
  }, [
    shapesManager.renderedSheetShapes,
    selectDbIds.setSelectedDbIds,
    visibilityManager.hiddenElements,
    visibilityManager.hiddenSheetShapes,
    visibilityManager.isolatedElements,
  ]);

  const hideSelectedHandler = useCallback(() => {
    visibilityManager.hide(selectDbIds.selectedDbIds);
    selectDbIds.setSelectedDbIds({});
  }, [
    selectDbIds.selectedDbIds,
    selectDbIds.setSelectedDbIds,
    visibilityManager,
  ]);

  const isolateSelectedHandler = useCallback(() => {
    visibilityManager.setIsolatedElements(selectDbIds.selectedDbIds);
  }, [selectDbIds.selectedDbIds, visibilityManager]);

  const hideSelectionRelatedItems = useCallback(
    ({ props }: PredicateParams<SheetViewerV2MenuProps>) => {
      if (!props) {
        return true;
      } else {
        return !props.hasSelectedElements;
      }
    },
    []
  );

  // Register context menu items
  useEffect(() => {
    registerItem({
      id: 'isolateSelected',
      label: t('viewer-menu.isolate-selected'),
      onClick: isolateSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon as={IsolationIcon} />,
    });
    return () => {
      unregisterItem('isolateSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    isolateSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'hideSelected',
      label: t('viewer-menu.hide-selected'),
      onClick: hideSelectedHandler,
      hidden: hideSelectionRelatedItems,
      group: 'viewer visibility',
      icon: <SparkelIcon size="sm" fixedWidth icon={faEyeSlash} />,
    });
    return () => {
      unregisterItem('hideSelected');
    };
  }, [
    hideSelectionRelatedItems,
    registerItem,
    hideSelectedHandler,
    unregisterItem,
    t,
  ]);

  useEffect(() => {
    registerItem({
      id: 'selectAllVisible',
      label: t('viewer-menu.select-visible'),
      onClick: selectAllVisibleHandler,
      group: 'viewer selection',
      icon: <SparkelIcon size="sm" fixedWidth icon={faMousePointer} />,
    });
    return () => {
      unregisterItem('selectAllVisible');
    };
  }, [registerItem, selectAllVisibleHandler, t, unregisterItem]);

  // Apply theme when color mode changes
  useEffect(() => {
    if (aggregatedView && viewerMode === ViewerMode.SheetsV2) {
      setTheme(aggregatedView, { colorMode });
    }
  }, [aggregatedView, colorMode, viewerMode]);

  // One-time auto-activation of drawing mode based on URL param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autoDraw = params.get('autoDraw');
    if (
      viewerMode === ViewerMode.SheetsV2 &&
      activeSheetContext.activeSheet?.data &&
      autoDraw &&
      activeTool === ActiveTool.Default
    ) {
      if (autoDraw === 'magicPolygon') {
        setActiveTool(ActiveTool.Drawing);
        drawing.setDrawingMode(DrawingMode.MagicPolygon);
      }
      params.delete('autoDraw');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [
    location.search,
    viewerMode,
    activeSheetContext.activeSheet,
    setActiveTool,
    drawing,
    navigate,
    activeTool,
  ]);

  const loadingTextColor = useColorModeValue('gray.500', 'gray.300');

  return (
    <SheetsViewerV2Context.Provider
      value={{
        viewer: {
          elementRef: viewerRef,
          aggregated: aggregatedView,
        },
        projectId,
        viewerMode,
        calibration,
        shapesManager,
        visibilityManager,
        autoHiddenLegends,
        measurement,
        drawing,
        pdfGhosting: {
          opacity: pdfGhosting.opacity,
          setOpacity: pdfGhosting.setOpacity,
        },
        ...activeSheetContext,
        ...selectDbIds,
        ...activeToolContext,
        showSheetShapeLabels,
        setShowSheetShapeLabels,
        colorization,
        propertyData,
        isLoading,
      }}
    >
      <>
        <Fade
          in={
            isLoading && !shouldHideViewer && viewerMode === ViewerMode.SheetsV2
          }
          unmountOnExit
        >
          <AbsoluteCenter axis="both" textAlign={'center'}>
            <Spinner color="brand.400" />
            <Text size="sm" color={loadingTextColor}>
              {t('viewer.loading-sheets')}
            </Text>
          </AbsoluteCenter>
        </Fade>

        {menuElement}
        {children}
        {sheetsViewerMode === ViewerMode.SheetsV2 && (
          <GridItem
            id={'sheets-viewer-v2'}
            position="relative"
            ref={viewerRef}
            zIndex={
              !shouldHideViewer && viewerMode === ViewerMode.SheetsV2 ? 1 : 0
            }
            opacity={
              !shouldHideViewer && viewerMode === ViewerMode.SheetsV2 ? 1 : 0
            }
            pointerEvents={shouldHideViewer ? 'none' : undefined}
            area="sidebar-left-start / sidebar-left-start / sidebar-right-end / sidebar-right-end"
            {...bindContextMenu()}
            sx={{
              '.adsk-viewing-viewer': {
                transition: 'opacity 0.8s ease-in-out',
                opacity: !isLoading ? 1 : 0,
                bottom: undefined,
              },
              '@media (max-width: 1200px)': {
                gridArea:
                  'sidebar-left-start / sidebar-left-start / main-end / main-end',
              },
            }}
            backgroundColor="gray.50"
          >
            <DragAndDrop
              uploadFunction={handleUploadSheetFiles}
              allowedMimeTypes={['application/pdf']}
              borderRadius="none"
              fill
            >
              {/* Hidden file input for upload button */}
              <input
                ref={sheetInputRef}
                type="file"
                multiple
                accept="application/pdf"
                onChange={handleSheetInputChange}
                style={{ display: 'none' }}
              />

              {/* Show NoSheetsPlaceholder when there are no sheets */}
              {!isLoading &&
                activeSheetContext.sheets &&
                activeSheetContext.sheets.length === 0 &&
                !shouldHideViewer &&
                viewerMode === ViewerMode.SheetsV2 && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    zIndex={100}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    paddingTop={'15%'}
                  >
                    <Image
                      alt="sheets placeholder"
                      objectFit="contain"
                      src={noSheetsPlaceholder}
                      maxHeight={100}
                    />
                    <Heading color="gray.500" size="lg" as="h3" mt={4}>
                      {t('viewer.no-sheets-placeholder')}
                    </Heading>
                    <PlaceholderText>
                      {t('viewer.no-sheets-description')}
                    </PlaceholderText>
                    <Button
                      mt={4}
                      size="sm"
                      colorScheme="gray"
                      leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
                      onClick={() => sheetInputRef.current?.click()}
                    >
                      {t('files.add-sheet')}
                    </Button>
                  </Box>
                )}
            </DragAndDrop>
          </GridItem>
        )}
      </>
    </SheetsViewerV2Context.Provider>
  );
}
